import { z } from "zod";
import {
  CHILD_NAME_MAX,
  GRADE_MAX,
  NOTES_MAX,
  SCHOOL_NAME_MAX,
  PHONE_MAX,
  RELATIONSHIP_MAX,
  CONTACT_NAME_MAX,
  CHILD_AGE_MIN,
  CHILD_AGE_MAX,
  PHONE_REGEX,
} from "@shared/constants/validation.js";
import { calculateAge } from "@shared/lib/age.js";

// "YYYY-MM-DD", matching the Drizzle `date` column (mode: "string"). Age is
// computed from this rather than entered directly, so it never goes stale.
// Reused as-is for create and update — nullable().optional() already lets
// "not sent" (undefined) and "explicitly cleared" (null) pass through
// untouched, same distinction the update schema's other fields rely on.
const dateOfBirthField = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD format")
  .refine((v) => {
    const parsed = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === v;
  }, "Enter a real calendar date")
  .refine((v) => calculateAge(v) >= CHILD_AGE_MIN, "Date of birth can't be in the future")
  .refine((v) => calculateAge(v) <= CHILD_AGE_MAX, `Child must be ${CHILD_AGE_MAX} or younger`)
  .nullable()
  .optional();

// Create-time helpers: coerce missing/empty → null, applied AFTER validation.
const nullableTrimmedString = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null));

const createPhoneSchema = z
  .string()
  .trim()
  .max(PHONE_MAX, "Phone number is too long")
  .regex(PHONE_REGEX, "Phone number contains invalid characters")
  .nullish()
  .transform((v) => (v && v.length > 0 ? v : null));

/**
 * The optional full legal name (BRIEF §5.2). `name` is the NICKNAME — what
 * caregivers call the child — and stays the one required field. A legal name
 * is only needed when a caregiver may have to deal with a hospital or school,
 * and it is hidden on care links unless the parent ticks "Include legal name".
 */
export const LEGAL_NAME_MAX = 200;
const LEGAL_NAME_TOO_LONG = "Legal name is too long";

// Create: trimmed; missing, null or "" all become null.
const createLegalNameField = z
  .string()
  .trim()
  .max(LEGAL_NAME_MAX, LEGAL_NAME_TOO_LONG)
  .nullish()
  .transform((v) => (v && v.length > 0 ? v : null));

// Update: trimmed; "" becomes null (cleared), but an OMITTED field stays
// undefined so the service can tell "not sent" from "explicitly cleared".
const updateLegalNameField = z
  .string()
  .trim()
  .max(LEGAL_NAME_MAX, LEGAL_NAME_TOO_LONG)
  .nullable()
  .optional()
  .transform((v) => (v === "" ? null : v));

const nicknameField = z
  .string()
  .trim()
  .min(1, "A nickname is required — it's what caregivers call them")
  .max(CHILD_NAME_MAX, "Nickname is too long");

export const createChildSchema = z.object({
  name: nicknameField,
  legalName: createLegalNameField,
  dateOfBirth: dateOfBirthField,
  grade: nullableTrimmedString(GRADE_MAX),
  additionalNotes: nullableTrimmedString(NOTES_MAX),
  isEnrolled: z.boolean().default(true),
  schoolName: nullableTrimmedString(SCHOOL_NAME_MAX),
  schoolPhone: createPhoneSchema,
  teacherName: nullableTrimmedString(CONTACT_NAME_MAX),
  schoolNursePhone: createPhoneSchema,
  emergencyContactName: nullableTrimmedString(CONTACT_NAME_MAX),
  emergencyContactPhone: createPhoneSchema,
  emergencyContactRelationship: nullableTrimmedString(RELATIONSHIP_MAX),
  secondaryEmergencyContactName: nullableTrimmedString(CONTACT_NAME_MAX),
  secondaryEmergencyContactPhone: createPhoneSchema,
  secondaryEmergencyContactRelationship: nullableTrimmedString(RELATIONSHIP_MAX),
});

// Update schema: strictly optional fields with NO transforms, so omitted fields
// stay `undefined` (not `null`) and the service layer can tell "not sent" apart
// from "explicitly cleared". Fixes the partial-update null-silently bug caught
// in the Sprint 2 code review.
const updatePhoneSchema = z
  .string()
  .trim()
  .max(PHONE_MAX, "Phone number is too long")
  .regex(PHONE_REGEX, "Phone number contains invalid characters")
  .nullable()
  .optional();

const updateNullableString = (max: number) =>
  z.string().trim().max(max).nullable().optional();

export const updateChildSchema = z.object({
  name: nicknameField.optional(),
  legalName: updateLegalNameField,
  dateOfBirth: dateOfBirthField,
  grade: updateNullableString(GRADE_MAX),
  additionalNotes: updateNullableString(NOTES_MAX),
  isEnrolled: z.boolean().optional(),
  schoolName: updateNullableString(SCHOOL_NAME_MAX),
  schoolPhone: updatePhoneSchema,
  teacherName: updateNullableString(CONTACT_NAME_MAX),
  schoolNursePhone: updatePhoneSchema,
  emergencyContactName: updateNullableString(CONTACT_NAME_MAX),
  emergencyContactPhone: updatePhoneSchema,
  emergencyContactRelationship: updateNullableString(RELATIONSHIP_MAX),
  secondaryEmergencyContactName: updateNullableString(CONTACT_NAME_MAX),
  secondaryEmergencyContactPhone: updatePhoneSchema,
  secondaryEmergencyContactRelationship: updateNullableString(RELATIONSHIP_MAX),
});

export type CreateChildInput = z.infer<typeof createChildSchema>;
export type UpdateChildInput = z.infer<typeof updateChildSchema>;

// Child profile photo: a small, client-resized image data-URI. The 60KB cap
// keeps the request under the global 64KB JSON body limit; the client resizes
// before upload. Only used while PHOTO_UPLOADS_ENABLED is true: with the switch
// off, PUT /api/children/:id/photo is not mounted at all (standard 404).
export const childPhotoSchema = z.object({
  photoUrl: z
    .string()
    .regex(/^data:image\/(png|jpe?g|webp);base64,/, "Must be a PNG, JPEG, or WebP image")
    .max(60000, "Image is too large — please choose a smaller photo"),
});

export type ChildPhotoInput = z.infer<typeof childPhotoSchema>;
